import { QueryClient } from '@tanstack/react-query'

import type { ApiError } from './problem-details'

declare module '@tanstack/react-query' {
  interface Register {
    /** The API client normalises every failure, so no consumer ever handles an AxiosError. */
    defaultError: ApiError
  }
}

/**
 * Retrying a request the server has already judged — a 403, a 409, a business rule refusal — only
 * delays the answer the operator needs. Retries are therefore reserved for failures that plausibly
 * resolve themselves: a dropped link on a rural branch connection, or a server-side blip.
 */
const isWorthRetrying = (error: ApiError) =>
  error.isNetworkFailure || error.status === 0 || error.status >= 500

export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
        retry: (failureCount, error) => isWorthRetrying(error) && failureCount < 2,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        refetchOnWindowFocus: false,
      },
      mutations: {
        // A command that may have posted money is never retried automatically. The user decides,
        // and the server's idempotency key is what makes their retry safe.
        retry: false,
      },
    },
  })
